import { useState, useRef } from 'react';
import { Button } from './button';
import { Input } from './input';
import { Upload, Link, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check if URL is a Google Drive link
const isGoogleDriveUrl = (url) => {
  if (!url) return false;
  return url.includes('drive.google.com') || url.includes('drive.usercontent.google.com');
};

export default function ImageUpload({ 
  value, 
  onChange, 
  token,
  placeholder = "https://example.com/image.jpg",
  previewClassName = "w-32 h-20"
}) {
  const [mode, setMode] = useState('url'); // 'url' or 'upload'
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef(null);

  // Handle Google Drive URL - download and store locally
  const handleGoogleDriveUrl = async (url) => {
    if (!isGoogleDriveUrl(url)) return null;
    
    setDownloading(true);
    try {
      const response = await axios.post(`${API}/uploads/from-url`, { url }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.data.success) {
        toast.success('Image saved! You can now make the Google Drive file private again.');
        // Return the full URL to the uploaded file
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        return `${baseUrl}${response.data.url}`;
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to download from Google Drive';
      // Show specific instructions for permission error
      if (detail.includes('not publicly shared') || detail.includes('Cannot access')) {
        toast.error('Google Drive file not accessible. Please share it with "Anyone with the link" first.', {
          duration: 8000
        });
      } else {
        toast.error(detail);
      }
      console.error('Google Drive download error:', detail);
    } finally {
      setDownloading(false);
    }
    return null;
  };

  const handleUrlChange = async (e) => {
    const inputUrl = e.target.value.trim();
    
    // Show the URL immediately so user sees it's captured
    onChange(inputUrl);
    
    // If it's a Google Drive URL and looks complete, download it
    if (isGoogleDriveUrl(inputUrl) && inputUrl.includes('/d/')) {
      const localUrl = await handleGoogleDriveUrl(inputUrl);
      if (localUrl) {
        onChange(localUrl);
      }
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 10MB');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/uploads`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      onChange(response.data.url);
      toast.success('Image uploaded successfully');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-2">
      {/* Mode Toggle */}
      <div className="flex gap-1 mb-2">
        <Button
          type="button"
          variant={mode === 'url' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('url')}
          className="text-xs h-7"
        >
          <Link className="h-3 w-3 mr-1" />
          URL
        </Button>
        <Button
          type="button"
          variant={mode === 'upload' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('upload')}
          className="text-xs h-7"
        >
          <Upload className="h-3 w-3 mr-1" />
          Upload
        </Button>
      </div>

      {/* Input Area */}
      {mode === 'url' ? (
        <div className="space-y-1">
          <Input
            value={value || ''}
            onChange={handleUrlChange}
            placeholder={placeholder}
            disabled={downloading}
          />
          {downloading && (
            <p className="text-[10px] text-blue-600 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Downloading from Google Drive...
            </p>
          )}
          {value && !downloading && value.includes('drive.google.com') && (
            <p className="text-[10px] text-amber-600">
              ⚠️ Set Google Drive sharing to "Anyone with link", then re-paste URL
            </p>
          )}
          {value && !downloading && !value.includes('drive.google.com') && (
            <p className="text-[10px] text-green-600">✓ Image ready</p>
          )}
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileChange}
            className="hidden"
            id="image-upload"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-1"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Choose File
              </>
            )}
          </Button>
        </div>
      )}

      {/* Preview */}
      {value && (
        <div className="relative inline-block group">
          <div className={`${previewClassName} rounded overflow-hidden border bg-muted`}>
            <img 
              src={value} 
              alt="Preview" 
              className="w-full h-full object-cover"
              onError={(e) => {
                console.log('Image failed to load:', value);
                e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-xs text-muted-foreground">Image failed to load</div>';
              }}
              onLoad={() => {
                console.log('Image loaded successfully:', value);
              }}
            />
          </div>
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="h-3 w-3" />
          </button>
          <p className="text-[10px] text-muted-foreground mt-1 truncate max-w-[200px]" title={value}>
            {value.length > 40 ? value.substring(0, 40) + '...' : value}
          </p>
        </div>
      )}
    </div>
  );
}
